/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { Col, Divider, Empty, Form, Input, InputNumber, Row, Select, Slider, Spin, Switch, Typography } from 'antd';
import { t } from 'i18next';
import { InfoCircleOutlined } from '@ant-design/icons';

// Misc imports
import { EMBEDDING_PROVIDERS, TRANSLATION_PROVIDERS } from '../../../constants/main';
import useTheme from '../../../utils/hooks/useTheme';

// Constant declarations
const { Text } = Typography;
const { Option } = Select;

const HelviaGPTPipelineProviderForm = () => {
  return (
    <Fragment>
      <Divider orientation="left" className="mb-8">
        <Text type="secondary" className="fs-base">{t('Provider Settings')}</Text>
      </Divider>

      <Form.Item
        label={t('Service URL')}
        name="serviceUrl"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        },
        {
          type: 'url',
          message: `${t('This field must be a valid url')}.`
        }]}
      >
        <Input placeholder={t('Enter the service url')} />
      </Form.Item>

      <Form.Item
        label={t('Pipeline ID')}
        name="pipelineId"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input placeholder={t('Enter the pipeline id')} />
      </Form.Item>

      <Form.Item
        label={t('Access Token')}
        name="bearerToken"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input.TextArea
          autoSize={{ minRows: 6, maxRows: 6 }}
          rows={4}
          placeholder={t('Enter the access token')}
          allowClear
        />
      </Form.Item>
    </Fragment>
  );
};

const HelviaGPTPipelineProviderConfigurationForm = ({
  gptPipelineSettings,
  gptPipelineSettingsForm,
  isLoading,
  onSubmit,
  setHasPendingChanges,
  updateState
}) => {
  const temperatureSliderRef = useRef(null);
  const maxMessagesSliderRef = useRef(null);
  const maxResultsSliderRef = useRef(null);
  const { borderRadiusBase, formProps } = useTheme();

  return (
    gptPipelineSettings && !isLoading ? (
      <Fragment>
        <Divider orientation="left" className="mb-8">
          <Text type="secondary" className="fs-base">{t('General Settings')}</Text>
        </Divider>

        <Form
          form={gptPipelineSettingsForm}
          validateMessages={{
            required: t('Please enter a value'),
            whitespace: t('Value cannot be blank')
          }}
          initialValues={gptPipelineSettings ? gptPipelineSettings : {}}
          validateTrigger={['onBlur', 'onChange']}
          onFinish={onSubmit}
          onValuesChange={(_, allFields) => {
            setHasPendingChanges(true);
            updateState(allFields);
          }}
          labelCol={{ xs: formProps.mainColSpan }}
          wrapperCol={{ xs: formProps.secondaryColSpan }}
          labelAlign="left"
          className="px-3"
        >
          <Form.Item
            label={t('Article Format')}
            name={['configuration', 'general_settings', 'article_format']}
            tooltip={{
              title: (
                <Row>
                  <Col span={24}>
                    {t('Available placeholders:')}
                  </Col>
                  <Col span={24}>
                    {`- {{${t('title')}}}: ${'injects the title of the article'}`}
                  </Col>
                  <Col span={24}>
                    {`- {{${t('body')}}}: ${'injects the body of the article'}`}

                  </Col>
                  <Col span={24}>
                    {`- {{${t('group')}}}: ${'injects the group of the article'}`}

                  </Col>
                  <Col span={24}>
                    {`- {{${t('tags')}}}: ${'injects the tags of the article'}`}
                  </Col>
                  <Col span={24}>
                    {`- {{${t('examples')}}}: ${'injects examples of user input as defined in the article'}`}
                  </Col>
                </Row>
              ),
              icon: <InfoCircleOutlined />
            }}
          >
            <Input.TextArea
              placeholder={t('Enter the service url')}
            />
          </Form.Item>

          <Divider orientation="left" className="mb-8">
            <Text type="secondary" className="fs-base">{t('Semantic Search Configuration')}</Text>
          </Divider>

          <Form.Item
            label={t('Enabled')}
            name={['configuration', 'semantic_search', 'enabled']}
            valuePropName="checked"
          >
            <Switch/>
          </Form.Item>

          <div ref={maxResultsSliderRef}>
            <Form.Item
              label={t('Max results')}
              name={['configuration', 'semantic_search', 'max_results']}
            >
              <Slider
                id="semantic-search-slider"
                min={0}
                max={20}
                className="custom-slider"
                marks={{ 0: '0', 20: '20' }}
                handleStyle={{ borderRadius: borderRadiusBase }}
                onChange={(value) => {
                  const sliderHandler = maxResultsSliderRef.current.querySelector('.ant-slider-handle');
                  if (!sliderHandler) {
                    return;
                  }
                  sliderHandler.innerHTML = value;
                }}
              />
            </Form.Item>
          </div>

          <Form.Item
            label={t('Embeddings provider')}
            name={['configuration', 'semantic_search', 'platform']}
          >
            <Select
              className="w-100"
              placeholder={t('Choose a provider')}
              getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
            >
              {
                Object.entries(EMBEDDING_PROVIDERS)
                  .map(([key, value]) => (
                    <Option key={key} value={value}>{t(value, { context: 'embedding provider' })}</Option>
                  ))
              }
            </Select>
          </Form.Item>

          <Form.Item
            label={t('Embeddings Provider URL')}
            name={['configuration', 'semantic_search', 'platform_url']}
          >
            <Input placeholder={t('Enter the embeddings provider URL')} />
          </Form.Item>

          <Form.Item
            label={t('Embeddings Provider API Key')}
            name={['configuration', 'semantic_search', 'platform_api_key']}
          >
            <Input placeholder={t('Enter the embeddings provider API key')} />
          </Form.Item>

          <Divider orientation="left" className="mb-8">
            <Text type="secondary" className="fs-base">{t('LLM Configuration')}</Text>
          </Divider>

          <Form.Item
            label={t('Enabled')}
            name={['configuration', 'llm', 'enabled']}
            valuePropName="checked"
          >
            <Switch/>
          </Form.Item>

          <Form.Item
            label={t('Provider')}
            name={['configuration', 'llm', 'platform']}
          >
            <Select
              className="w-100"
              placeholder={t('Choose a provider')}
              getPopupContainer={triggerNode => triggerNode.parentElement}
            >
              {
                Object.entries(EMBEDDING_PROVIDERS)
                  .map(([key, value]) => (
                    <Option key={key} value={value}>{t(value, { context: 'embedding provider' })}</Option>
                  ))
              }
            </Select>
          </Form.Item>

          <Form.Item
            label={t('Provider URL')}
            name={['configuration', 'llm', 'platform_url']}
          >
            <Input placeholder={t('Enter the provider URL')} />
          </Form.Item>

          <Form.Item
            label={t('Provider API Key')}
            name={['configuration', 'llm', 'platform_api_key']}
          >
            <Input placeholder={t('Enter the provider API key')} />
          </Form.Item>

          <Form.Item
            label={t('Model')}
            name={['configuration', 'llm', 'model']}
          >
            <Input placeholder={t('Enter the model')} />
          </Form.Item>

          <Form.Item
            label={t('Prompt')}
            name={['configuration', 'llm', 'prompt']}
            tooltip={{
              title: (
                <Row>
                  <Col span={24}>
                    {t('Available placeholders')}:
                  </Col>
                  <Col>
                    {`- {{${t('corpus')}}}: ${t('injects the list of articles retrieved but the Semantic Search step, using the format specified in the General Settings')}`}
                  </Col>
                </Row>
              ),
              icon: <InfoCircleOutlined />
            }}
          >
            <Input.TextArea
              rows={8}
              placeholder={t('Enter the prompt')}
            />
          </Form.Item>

          <div ref={temperatureSliderRef}>
            <Form.Item
              label={t('Temperature')}
              name={['configuration', 'llm', 'temperature']}
            >
              <Slider
                min={0}
                max={2}
                step={0.1}
                className="custom-slider"
                marks={{ 0: '0', 2: '2' }}
                handleStyle={{ borderRadius: borderRadiusBase }}
                onChange={(value) => {
                  const sliderHandler = temperatureSliderRef.current.querySelector('.ant-slider-handle');
                  if (!sliderHandler) {
                    return;
                  }
                  sliderHandler.innerHTML = value;
                }}
              />
            </Form.Item>
          </div>

          <Form.Item
            label={t('Max Tokens')}
            name={['configuration', 'llm', 'max_tokens']}
          >
            <InputNumber min={1} />
          </Form.Item>

          <Divider orientation="left" className="mb-8">
            <Text type="secondary" className="fs-base">{t('Translation Configuration')}</Text>
          </Divider>

          <Form.Item
            label={t('Enabled')}
            name={['configuration', 'language_translation', 'enabled']}
            valuePropName="checked"
          >
            <Switch/>
          </Form.Item>

          <Row gutter={24}>
            <Col sm={24} md={12}>
              <Form.Item
                label={t('Corpus Translation Provider')}
                name={['configuration', 'language_translation', 'corpus_translation_provider']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Select
                  placeholder={t('Choose a provider')}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                >
                  {
                    Object.entries(TRANSLATION_PROVIDERS)
                      .map(([key, value]) => (
                        <Option key={key} value={value}>{t(value, { context: 'translation provider' })}</Option>
                      ))
                  }
                </Select>
              </Form.Item>
            </Col>

            <Col sm={24} md={12}>
              <Form.Item
                label={t('Location')}
                name={['configuration', 'language_translation', 'corpus_translation_provider_location']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Input placeholder={t('Enter the corpus translation provider location')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col sm={24} md={12}>
              <Form.Item
                label={t('Query Translation Provider')}
                name={['configuration', 'language_translation', 'query_translation_provider']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Select
                  className="w-100"
                  placeholder={t('Choose a provider')}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                >
                  {
                    Object.entries(TRANSLATION_PROVIDERS)
                      .map(([key, value]) => (
                        <Option key={key} value={value}>{t(value, { context: 'translation provider' })}</Option>
                      ))
                  }
                </Select>
              </Form.Item>
            </Col>

            <Col sm={24} md={12}>
              <Form.Item
                label={t('Location')}
                name={['configuration', 'language_translation', 'query_translation_provider_location']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Input placeholder={t('Enter the query translation provider location')} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" className="mb-8">
            <Text type="secondary" className="fs-base">{t('History Configuration')}</Text>
          </Divider>

          <Form.Item
            label={t('Enabled')}
            name={['configuration', 'chat_history', 'enabled']}
            valuePropName="checked"
          >
            <Switch/>
          </Form.Item>

          <div ref={maxMessagesSliderRef}>
            <Form.Item
              label={t('Max Messages')}
              name={['configuration', 'chat_history', 'max_messages']}
            >
              <Slider
                min={0}
                max={20}
                className="custom-slider"
                marks={{ 0: '0', 20: '20' }}
                handleStyle={{ borderRadius: borderRadiusBase }}
                onChange={(value) => {
                  const sliderHandler = maxMessagesSliderRef.current.querySelector('.ant-slider-handle');
                  if (!sliderHandler) {
                    return;
                  }
                  sliderHandler.innerHTML = value;
                }}
              />
            </Form.Item>
          </div>
        </Form>
      </Fragment>
    ) : !gptPipelineSettings && isLoading ? (
      <Row justify="center">
        <Col span={24}>
          <Spin className="d-block" size="large" spinning />
        </Col>
      </Row>
    ): (
      <Row className="h-100" align="middle" justify="center">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('Please fill in the Provider Settings to access the NLP Configuration.')}
          style={{ maxWidth: 300 }}
        />
      </Row>
    )
  );
};

HelviaGPTPipelineProviderConfigurationForm.propTypes = {
  gptPipelineSettings: PropTypes.object,
  gptPipelineSettingsForm: PropTypes.func,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  setHasPendingChanges: PropTypes.func,
  updateState: PropTypes.func
};

export { HelviaGPTPipelineProviderForm, HelviaGPTPipelineProviderConfigurationForm };
